import React, {
    useState,
    useEffect,
    useReducer,
    useCallback,
    useContext,
    useMemo,
} from "react";
import { toast } from "react-toastify";
import moment from "moment";
import { useHistory } from "react-router-dom";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableContainer from "@material-ui/core/TableContainer";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Checkbox from "@material-ui/core/Checkbox";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import DeleteSweepIcon from "@material-ui/icons/DeleteSweep";
import WarningIcon from "@material-ui/icons/Warning";
import AssessmentIcon from "@material-ui/icons/Assessment";
import FolderIcon from "@material-ui/icons/Folder";
import AssignmentIcon from "@material-ui/icons/Assignment";
import AudiotrackIcon from "@material-ui/icons/Audiotrack";
import VideocamIcon from "@material-ui/icons/Videocam";
import ImageIcon from "@material-ui/icons/Image";
import DescriptionIcon from "@material-ui/icons/Description";
import RefreshIcon from "@material-ui/icons/Refresh";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import ChatIcon from "@material-ui/icons/Chat";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import QuestionAnswerIcon from "@material-ui/icons/QuestionAnswer";
import PeopleIcon from "@material-ui/icons/People";
import SendIcon from "@material-ui/icons/Send";
import CloseIcon from "@material-ui/icons/Close";
import BrokenImageIcon from "@material-ui/icons/BrokenImage";

import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import LinearProgress from "@material-ui/core/LinearProgress";
import Box from "@material-ui/core/Box";
import Divider from "@material-ui/core/Divider";
import List from "@material-ui/core/List";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import FileModal from "../../components/FileModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import PageHelpGuide from "../../components/PageHelpGuide";
import StandardTabs from "../../components/StandardTabs";
import { FILES_HELP_DATA } from "../../constants/pageHelp/filesHelp";
import { getStandardButtonStyles } from "../../styles/standardButtons";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";

const reducer = (state, action) => {
    if (action.type === "LOAD_FILES") {
        const files = action.payload;
        const newFiles = [];

        files.forEach((fileList) => {
            const fileListIndex = state.findIndex((s) => s.id === fileList.id);
            if (fileListIndex !== -1) {
                state[fileListIndex] = fileList;
            } else {
                newFiles.push(fileList);
            }
        });

        return [...state, ...newFiles];
    }

    if (action.type === "UPDATE_FILES") {
        const fileList = action.payload;
        const fileListIndex = state.findIndex((s) => s.id === fileList.id);

        if (fileListIndex !== -1) {
            state[fileListIndex] = fileList;
            return [...state];
        } else {
            return [fileList, ...state];
        }
    }

    if (action.type === "DELETE_FILE") {
        const fileListId = action.payload;

        const fileListIndex = state.findIndex((s) => s.id === fileListId);
        if (fileListIndex !== -1) {
            state.splice(fileListIndex, 1);
        }
        return [...state];
    }

    if (action.type === "DELETE_BATCH") {
        const ids = action.payload;
        return state.filter(f => !ids.includes(f.id));
    }

    if (action.type === "RESET") {
        return [];
    }
};

const useStyles = makeStyles((theme) => ({
    ...getStandardButtonStyles(theme),
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1.5),
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: 16,
        backgroundColor: theme.palette.type === 'dark' ? '#2A2D32 !important' : theme.palette.background.paper,
    },
    tablePaper: {
        flex: 1,
        minWidth: 0,
        padding: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: "transparent",
    },
    tableHeader: {
        backgroundColor: theme.palette.type === 'dark' ? '#1e1e1e' : '#f5f5f5',
        color: theme.palette.type === 'dark' ? '#fff' : '#000',
        borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        position: "sticky",
        top: 0,
        zIndex: 1,
        fontWeight: 'bold',
    },
    tableHeaderSort: {
        color: "inherit",
        fontWeight: "bold",
        "&:hover": {
            color: `${theme.palette.primary.main} !important`,
        },
        "&.MuiTableSortLabel-active": {
            color: `${theme.palette.primary.main} !important`,
        },
        "& .MuiTableSortLabel-icon": {
            color: `${theme.palette.primary.main} !important`,
        },
    },
    scrollArea: {
        flex: 1,
        overflowY: "auto",
        overflowX: "auto",
        ...theme.scrollbarStyles,
    },
    sidebarPaper: {
        width: 320,
        minWidth: 320,
        maxWidth: 320,
        flex: "0 0 320px",
        padding: theme.spacing(1),
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        height: "100%",
        borderRadius: 12,
    },
    statsPaper: {
        padding: theme.spacing(1.2, 2),
        marginBottom: theme.spacing(1.5),
        borderRadius: 10,
    },
    alertCard: {
        backgroundColor: "rgba(232, 65, 24, 0.12)",
        border: "1px solid #e84118",
        borderRadius: 10,
        padding: theme.spacing(1, 2),
        marginBottom: theme.spacing(1.5),
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: theme.spacing(1.5)
    },
    clickableMetric: {
        cursor: "pointer",
        padding: theme.spacing(0.6, 1),
        borderRadius: 8,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
            backgroundColor: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
            transform: "translateY(-1px)",
        }
    },
    activeMetric: {
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(0, 176, 255, 0.15)' : 'rgba(0, 176, 255, 0.1)',
        borderLeft: "3px solid #00b0ff",
        padding: theme.spacing(0.6, 1),
        borderRadius: 8,
    },
    tabBar: {
        marginBottom: theme.spacing(1.5),
    },
    batchActionBar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: theme.palette.type === 'dark' ? '#1f242d' : '#f0f4f8',
        padding: theme.spacing(1, 2),
        borderRadius: 8,
        marginBottom: theme.spacing(1.5),
        border: "1px solid rgba(0, 176, 255, 0.3)",
    },
    filterControl: {
        minWidth: 160,
        marginRight: theme.spacing(1.5),
        marginTop: 4,
        marginBottom: 4,
    },
    sizePill: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "3px 10px",
        borderRadius: 12,
        fontSize: "0.75rem",
        fontWeight: 500,
        whiteSpace: "nowrap",
        border: theme.palette.type === "dark" 
            ? "1px solid rgba(255, 255, 255, 0.3)" 
            : "1px solid rgba(0, 0, 0, 0.25)",
        backgroundColor: theme.palette.type === "dark"
            ? "rgba(255, 255, 255, 0.06)"
            : "rgba(0, 0, 0, 0.04)",
        color: theme.palette.type === "dark" ? "#e0e0e0" : "#333333",
        lineHeight: "1.2",
        boxSizing: "border-box",
    },
    sizePillHeavy: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "3px 10px",
        borderRadius: 12,
        fontSize: "0.75rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
        border: "1px solid #e84118",
        backgroundColor: "rgba(232, 65, 24, 0.1)",
        color: "#e84118",
        lineHeight: "1.2",
        boxSizing: "border-box",
    },
    filePreview: {
        width: "100%",
        maxWidth: "200px",
        maxHeight: "170px",
        objectFit: "contain",
        height: "auto",
        marginBottom: theme.spacing(1),
        borderRadius: "8px",
        border: theme.palette.type === "dark" ? "1px solid rgba(255,255,255,0.12)" : "1px solid #ddd",
    },
    pdfPreview: {
        width: "100%",
        height: "300px",
        border: "none",
        borderRadius: "8px",
        marginBottom: theme.spacing(1),
    },
    audioPreview: {
        width: "100%",
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    copyInput: {
        width: "100%",
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        backgroundColor: theme.palette.background.default,
    },
    dangerButton: {
        backgroundColor: "#e84118 !important",
        color: "#fff !important",
        fontWeight: "bold !important",
        textTransform: "none !important",
        "&:hover": {
            backgroundColor: "#c23616 !important",
        }
    },
    purgeButton: {
        backgroundColor: "#9c27b0 !important",
        color: "#fff !important",
        fontWeight: "bold !important",
        textTransform: "none !important",
        "&:hover": {
            backgroundColor: "#7b1fa2 !important",
        }
    },
    infoPaper: {
        padding: theme.spacing(4),
        textAlign: "center",
        borderRadius: 12,
        backgroundColor: theme.palette.type === 'dark' ? '#22252a' : '#f8f9fa',
        border: `1px dashed ${theme.palette.divider}`,
        marginTop: theme.spacing(2),
    }
}));

const FileNotFoundFallback = () => {
    const theme = useTheme();
    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            p={2}
            textAlign="center"
            style={{ width: "100%", minHeight: 130 }}
        >
            <BrokenImageIcon style={{ fontSize: 44, color: "#95a5a6", marginBottom: 8 }} />
            <Typography variant="body2" style={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: "0.82rem" }}>
                {i18n.t("files.sidebar.fileNotFoundTitle")}
            </Typography>
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, fontSize: "0.72rem", lineHeight: 1.3 }}>
                {i18n.t("files.sidebar.fileNotFoundDescription")}
            </Typography>
        </Box>
    );
};

const ImageWithFallback = ({ src, alt, className, style }) => {
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        setHasError(false);
    }, [src]);

    if (hasError) {
        return <FileNotFoundFallback />;
    }

    return (
        <img
            src={src}
            alt={alt}
            className={className}
            style={style}
            onError={() => setHasError(true)}
        />
    );
};

const VideoWithFallback = ({ src, className, style }) => {
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        setHasError(false);
    }, [src]);

    if (hasError) {
        return <FileNotFoundFallback />;
    }

    return (
        <video
            src={src}
            controls
            className={className}
            style={style}
            onError={() => setHasError(true)}
        />
    );
};

const FileLists = () => {
    const classes = useStyles();
    const theme = useTheme();
    const history = useHistory();
    const { user, socket } = useContext(AuthContext);

    // Active Category Tab: 0=Archivos, 1=Mensajes, 2=Masivos, 3=Flujos, 4=Resp. Rápidas, 5=Contactos
    const [activeTab, setActiveTab] = useState(0);

    // States for Tab 0: Archivos
    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [selectedFileList, setSelectedFileList] = useState(null);
    const [deletingFileList, setDeletingFileList] = useState(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [searchParam, setSearchParam] = useState("");
    const [files, dispatch] = useReducer(reducer, []);
    const [fileListModalOpen, setFileListModalOpen] = useState(false);
    const [selectedFileId, setSelectedFileId] = useState(null);

    // Checkboxes selection for Tab 0
    const [selectedFileIds, setSelectedFileIds] = useState([]);
    const [batchDeleteFilesModalOpen, setBatchDeleteFilesModalOpen] = useState(false);

    // Storage Stats
    const [storageStats, setStorageStats] = useState(null);

    // States for Tab 1: Mensajes
    const [messages, setMessages] = useState([]);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [messagesPage, setMessagesPage] = useState(1);
    const [messagesHasMore, setMessagesHasMore] = useState(false);
    const [messagesCount, setMessagesCount] = useState(0);
    const [searchMessageParam, setSearchMessageParam] = useState("");
    const [filterAge, setFilterAge] = useState("all");
    const [filterType, setFilterType] = useState("all");
    const [selectedMessageIds, setSelectedMessageIds] = useState([]);
    const [selectedMessageId, setSelectedMessageId] = useState(null);
    const [deletingMessage, setDeletingMessage] = useState(null);
    const [confirmDeleteMessageModalOpen, setConfirmDeleteMessageModalOpen] = useState(false);
    const [batchDeleteMessagesModalOpen, setBatchDeleteMessagesModalOpen] = useState(false);
    const [purgeDialogOpen, setPurgeDialogOpen] = useState(false);
    const [purgeDays, setPurgeDays] = useState(60);
    const [purgeType, setPurgeType] = useState("all");

    // Format Bytes helper
    const formatBytes = (bytes, decimals = 2) => {
        if (!+bytes) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
    };

    // Calculate total size of a File container
    const getFileListTotalSize = (fileList) => {
        if (!fileList || !fileList.options) return 0;
        return fileList.options.reduce((acc, opt) => acc + (opt.size || 0), 0);
    };

    // Sorting states for Tab 0: Archivos
    const [filesOrderBy, setFilesOrderBy] = useState("name");
    const [filesOrder, setFilesOrder] = useState("asc");

    const handleRequestSortFiles = (property) => {
        const isAsc = filesOrderBy === property && filesOrder === "asc";
        setFilesOrder(isAsc ? "desc" : "asc");
        setFilesOrderBy(property);
    };

    const sortedFiles = useMemo(() => {
        if (!files || files.length === 0) return [];
        return [...files].sort((a, b) => {
            let res = 0;
            if (filesOrderBy === "name") {
                res = (a.name || "").localeCompare(b.name || "", undefined, { numeric: true, sensitivity: "base" });
            } else if (filesOrderBy === "size") {
                const sizeA = getFileListTotalSize(a);
                const sizeB = getFileListTotalSize(b);
                res = sizeA - sizeB;
            } else if (filesOrderBy === "createdAt") {
                res = new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
            }
            return filesOrder === "asc" ? res : -res;
        });
    }, [files, filesOrderBy, filesOrder]);

    // Sorting states for Tab 1: Mensajes
    const [messagesOrderBy, setMessagesOrderBy] = useState("createdAt");
    const [messagesOrder, setMessagesOrder] = useState("desc");

    const handleRequestSortMessages = (property) => {
        const isAsc = messagesOrderBy === property && messagesOrder === "asc";
        setMessagesOrder(isAsc ? "desc" : "asc");
        setMessagesOrderBy(property);
    };

    const sortedMessages = useMemo(() => {
        if (!messages || messages.length === 0) return [];
        return [...messages].sort((a, b) => {
            let res = 0;
            if (messagesOrderBy === "contact") {
                const contactA = a.contact?.name || a.contact?.number || (a.fromMe ? "Saliente" : "Entrante");
                const contactB = b.contact?.name || b.contact?.number || (b.fromMe ? "Saliente" : "Entrante");
                res = contactA.localeCompare(contactB, undefined, { numeric: true, sensitivity: "base" });
            } else if (messagesOrderBy === "name") {
                const nameA = a.fileName || (a.mediaUrl ? a.mediaUrl.split("/").pop() : a.body) || "";
                const nameB = b.fileName || (b.mediaUrl ? b.mediaUrl.split("/").pop() : b.body) || "";
                res = nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
            } else if (messagesOrderBy === "size") {
                const sizeA = a.fileSize || 0;
                const sizeB = b.fileSize || 0;
                res = sizeA - sizeB;
            } else if (messagesOrderBy === "createdAt") {
                res = new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
            }
            return messagesOrder === "asc" ? res : -res;
        });
    }, [messages, messagesOrderBy, messagesOrder]);

    // Auto-select first file for preview
    useEffect(() => {
        if (sortedFiles.length > 0 && !selectedFileId) {
            setSelectedFileId(sortedFiles[0].id);
        }
    }, [sortedFiles, selectedFileId]);

    const selectedFileForPreview = sortedFiles.find(f => f.id === selectedFileId);

    // Auto-select first message for preview
    useEffect(() => {
        if (sortedMessages.length > 0 && !selectedMessageId) {
            setSelectedMessageId(sortedMessages[0].id);
        }
    }, [sortedMessages, selectedMessageId]);

    const selectedMessageForPreview = sortedMessages.find(m => m.id === selectedMessageId);

    // Fetch storage metrics
    const fetchStorageStats = useCallback(async () => {
        try {
            const { data } = await api.get("/files/storage");
            setStorageStats(data);
        } catch (err) {
            toastError(err);
        }
    }, []);

    useEffect(() => {
        fetchStorageStats();
    }, [fetchStorageStats]);

    // Fetch Tab 0 Files
    const fetchFileLists = useCallback(async () => {
        try {
            const { data } = await api.get("/files/", {
                params: { searchParam, pageNumber },
            });
            dispatch({ type: "LOAD_FILES", payload: data.files });
            setHasMore(data.hasMore);
            setLoading(false);
        } catch (err) {
            toastError(err);
        }
    }, [searchParam, pageNumber]);

    useEffect(() => {
        dispatch({ type: "RESET" });
        setSelectedFileIds([]);
        setPageNumber(1);
    }, [searchParam]);

    useEffect(() => {
        if (activeTab === 0) {
            setLoading(true);
            const delayDebounceFn = setTimeout(() => {
                fetchFileLists();
            }, 500);
            return () => clearTimeout(delayDebounceFn);
        }
    }, [searchParam, pageNumber, fetchFileLists, activeTab]);

    // Fetch Tab 1 Messages Media
    const fetchMessageMedias = useCallback(async (page = 1) => {
        setMessagesLoading(true);
        try {
            const { data } = await api.get("/files/messages", {
                params: {
                    pageNumber: page,
                    searchParam: searchMessageParam,
                    mediaType: filterType,
                    daysOlderThan: filterAge !== "all" ? filterAge : undefined,
                }
            });
            if (page === 1) {
                setMessages(data.messages);
            } else {
                setMessages(prev => [...prev, ...data.messages]);
            }
            setMessagesHasMore(data.hasMore);
            setMessagesCount(data.count);
        } catch (err) {
            toastError(err);
        } finally {
            setMessagesLoading(false);
        }
    }, [searchMessageParam, filterType, filterAge]);

    useEffect(() => {
        if (activeTab === 1) {
            setSelectedMessageIds([]);
            setMessagesPage(1);
            fetchMessageMedias(1);
        }
    }, [activeTab, searchMessageParam, filterType, filterAge, fetchMessageMedias]);

    // Socket Listeners
    useEffect(() => {
        const onFileEvent = (data) => {
            if (data.action === "update" || data.action === "create") {
                dispatch({ type: "UPDATE_FILES", payload: data.fileList || data.files });
            }

            if (data.action === "delete") {
                dispatch({ type: "DELETE_FILE", payload: +data.fileId });
                if (selectedFileId === +data.fileId) {
                    setSelectedFileId(null);
                }
            }

            if (data.action === "deleteBatch") {
                dispatch({ type: "DELETE_BATCH", payload: data.fileIds });
            }
        };

        const onStorageEvent = (data) => {
            if (data.action === "update") {
                fetchStorageStats();
            }
        };

        socket.on(`company-${user.companyId}-file`, onFileEvent);
        socket.on(`company-${user.companyId}-storage`, onStorageEvent);
        return () => {
            socket.off(`company-${user.companyId}-file`, onFileEvent);
            socket.off(`company-${user.companyId}-storage`, onStorageEvent);
        };
    }, [socket, user.companyId, selectedFileId, fetchStorageStats]);

    // File Handlers (Tab 0)
    const handleOpenFileListModal = () => {
        setSelectedFileList(null);
        setFileListModalOpen(true);
    };

    const handleCloseFileListModal = () => {
        setSelectedFileList(null);
        setFileListModalOpen(false);
    };

    const handleEditFileList = (fileList) => {
        setSelectedFileList(fileList);
        setFileListModalOpen(true);
    };

    const handleDeleteFileList = async (fileListId) => {
        try {
            await api.delete(`/files/${fileListId}`);
            toast.success(i18n.t("files.toasts.deleted"));
            fetchStorageStats();
        } catch (err) {
            toastError(err);
        }
        setDeletingFileList(null);
        setSelectedFileIds(prev => prev.filter(id => id !== fileListId));
    };

    // Checkbox toggles for Tab 0
    const handleSelectAllFiles = (event) => {
        if (event.target.checked) {
            const allIds = files.map(f => f.id);
            setSelectedFileIds(allIds);
        } else {
            setSelectedFileIds([]);
        }
    };

    const handleToggleSelectFile = (fileId) => {
        setSelectedFileIds(prev =>
            prev.includes(fileId) ? prev.filter(id => id !== fileId) : [...prev, fileId]
        );
    };

    const handleDeleteBatchFiles = async () => {
        try {
            await api.post("/files/batch-delete", { fileIds: selectedFileIds });
            toast.success(i18n.t("files.bulk.deletedSuccess", { count: selectedFileIds.length }));
            setSelectedFileIds([]);
            setBatchDeleteFilesModalOpen(false);
            dispatch({ type: "DELETE_BATCH", payload: selectedFileIds });
            fetchStorageStats();
            await fetchFileLists();
        } catch (err) {
            toastError(err);
        }
    };

    // Messages Handlers (Tab 1)
    const handleSelectAllMessages = (event) => {
        if (event.target.checked) {
            setSelectedMessageIds(messages.map(m => m.id));
        } else {
            setSelectedMessageIds([]);
        }
    };

    const handleToggleSelectMessage = (messageId) => {
        setSelectedMessageIds(prev =>
            prev.includes(messageId) ? prev.filter(id => id !== messageId) : [...prev, messageId]
        );
    };

    const handleDeleteSingleMessage = async () => {
        if (!deletingMessage) return;
        try {
            await api.post("/files/messages/batch-delete", { messageIds: [deletingMessage.id] });
            toast.success(i18n.t("files.toasts.deleted"));
            setMessages(prev => prev.filter(m => m.id !== deletingMessage.id));
            setMessagesCount(prev => Math.max(0, prev - 1));
            setSelectedMessageIds(prev => prev.filter(id => id !== deletingMessage.id));
            fetchStorageStats();
        } catch (err) {
            toastError(err);
        } finally {
            setDeletingMessage(null);
            setConfirmDeleteMessageModalOpen(false);
        }
    };

    const handleDeleteBatchMessages = async () => {
        try {
            const { data } = await api.post("/files/messages/batch-delete", { messageIds: selectedMessageIds });
            toast.success(i18n.t("files.messagesTab.purgedSuccess", {
                count: data.deletedCount,
                size: formatBytes(data.freedBytes)
            }));
            setMessages(prev => prev.filter(m => !selectedMessageIds.includes(m.id)));
            setMessagesCount(prev => Math.max(0, prev - data.deletedCount));
            setSelectedMessageIds([]);
            setBatchDeleteMessagesModalOpen(false);
            fetchStorageStats();
        } catch (err) {
            toastError(err);
        }
    };

    const handlePurgeOldMessages = async () => {
        try {
            const { data } = await api.post("/files/messages/batch-delete", {
                purgeOlderThanDays: purgeDays,
                mediaType: purgeType !== "all" ? purgeType : undefined
            });
            toast.success(i18n.t("files.messagesTab.purgedSuccess", {
                count: data.deletedCount,
                size: formatBytes(data.freedBytes)
            }));
            setPurgeDialogOpen(false);
            fetchMessageMedias(1);
            fetchStorageStats();
        } catch (err) {
            toastError(err);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success(i18n.t("files.toasts.linkCopied") || "Link copied!");
    };

    const getFileUrl = (path) => {
        if (!path) return "";
        const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";
        if (path.startsWith("http")) {
            const publicIndex = path.indexOf("/public");
            if (publicIndex !== -1) {
                return `${backendUrl}${path.substring(publicIndex)}`;
            }
            if (path.includes("localhost")) {
                try {
                    const urlObj = new URL(path);
                    return `${backendUrl}${urlObj.pathname}`;
                } catch (e) {
                    return path.replace("http://localhost:8080", backendUrl).replace("http://localhost:3000", backendUrl);
                }
            }
            return path;
        }
        if (path.startsWith("/public")) {
            return `${backendUrl}${path}`;
        }
        if (path.startsWith("/")) {
            return `${backendUrl}${path}`;
        }
        return `${backendUrl}/public/${path}`;
    };

    const renderMediaTypeIcon = (mediaType) => {
        if (!mediaType) return <DescriptionIcon />;
        if (mediaType.includes("audio")) return <AudiotrackIcon style={{ color: "#ff9f43" }} />;
        if (mediaType.includes("video")) return <VideocamIcon style={{ color: "#0abde3" }} />;
        if (mediaType.includes("image")) return <ImageIcon style={{ color: "#10ac84" }} />;
        return <DescriptionIcon style={{ color: "#5f27cd" }} />;
    };

    // Calculate selected files total size
    const selectedFilesTotalSize = files
        .filter(f => selectedFileIds.includes(f.id))
        .reduce((sum, f) => sum + getFileListTotalSize(f), 0);

    const isStorageFull = storageStats && storageStats.limit > 0 && storageStats.used >= storageStats.limit;

    const getStoragePercentage = (used, limit) => {
        if (!limit || limit <= 0) return "0%";
        const pct = (used / limit) * 100;
        if (used >= limit) {
            return `${Math.round(pct)}%`;
        }
        if (pct >= 99) {
            return `${(Math.floor(pct * 10) / 10).toFixed(1)}%`;
        }
        return `${Math.round(pct)}%`;
    };

    return (
        <MainContainer>
            {/* Modal de confirmación para eliminar archivo individual */}
            <ConfirmationModal
                title={deletingFileList && `${i18n.t("files.confirmationModal.deleteTitle")}`}
                open={confirmModalOpen}
                onClose={setConfirmModalOpen}
                onConfirm={() => handleDeleteFileList(deletingFileList.id)}
            >
                {i18n.t("files.confirmationModal.deleteMessage")}
            </ConfirmationModal>

            {/* Modal de confirmación para eliminación múltiple de archivos */}
            <ConfirmationModal
                title={i18n.t("files.bulk.confirmTitle")}
                open={batchDeleteFilesModalOpen}
                onClose={setBatchDeleteFilesModalOpen}
                onConfirm={handleDeleteBatchFiles}
            >
                {i18n.t("files.bulk.confirmMessage", {
                    count: selectedFileIds.length,
                    size: formatBytes(selectedFilesTotalSize)
                })}
            </ConfirmationModal>

            {/* Modal de confirmación para eliminar media de mensaje individual */}
            <ConfirmationModal
                title={i18n.t("files.confirmationModal.deleteTitle")}
                open={confirmDeleteMessageModalOpen}
                onClose={setConfirmDeleteMessageModalOpen}
                onConfirm={handleDeleteSingleMessage}
            >
                {i18n.t("files.messagesTab.purgeDialogMessage", { days: "0" })}
            </ConfirmationModal>

            {/* Modal de confirmación para eliminación en bloque de mensajes */}
            <ConfirmationModal
                title={i18n.t("files.bulk.confirmTitle")}
                open={batchDeleteMessagesModalOpen}
                onClose={setBatchDeleteMessagesModalOpen}
                onConfirm={handleDeleteBatchMessages}
            >
                {i18n.t("files.bulk.confirmMessage", {
                    count: selectedMessageIds.length,
                    size: "los archivos seleccionados"
                })}
            </ConfirmationModal>

            {/* Diálogo de Depuración Guiada de Mensajes Antiguos */}
            <Dialog open={purgeDialogOpen} onClose={() => setPurgeDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <DeleteSweepIcon style={{ color: "#9c27b0", fontSize: 28 }} />
                        <Typography variant="h6">{i18n.t("files.messagesTab.purgeDialogTitle")}</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {i18n.t("files.messagesTab.purgeDialogMessage", { days: purgeDays })}
                    </DialogContentText>
                    <Box mt={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <FormControl variant="outlined" fullWidth size="small">
                                    <InputLabel>{i18n.t("files.messagesTab.filterAge")}</InputLabel>
                                    <Select
                                        value={purgeDays}
                                        onChange={(e) => setPurgeDays(e.target.value)}
                                        label={i18n.t("files.messagesTab.filterAge")}
                                    >
                                        <MenuItem value={30}>{i18n.t("files.messagesTab.age30")}</MenuItem>
                                        <MenuItem value={60}>{i18n.t("files.messagesTab.age60")}</MenuItem>
                                        <MenuItem value={90}>{i18n.t("files.messagesTab.age90")}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl variant="outlined" fullWidth size="small">
                                    <InputLabel>{i18n.t("files.messagesTab.filterType")}</InputLabel>
                                    <Select
                                        value={purgeType}
                                        onChange={(e) => setPurgeType(e.target.value)}
                                        label={i18n.t("files.messagesTab.filterType")}
                                    >
                                        <MenuItem value="all">{i18n.t("files.messagesTab.typeAll")}</MenuItem>
                                        <MenuItem value="audio">{i18n.t("files.messagesTab.typeAudio")}</MenuItem>
                                        <MenuItem value="video">{i18n.t("files.messagesTab.typeVideo")}</MenuItem>
                                        <MenuItem value="image">{i18n.t("files.messagesTab.typeImage")}</MenuItem>
                                        <MenuItem value="application">{i18n.t("files.messagesTab.typeDocument")}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPurgeDialogOpen(false)} color="default">
                        Cancelar
                    </Button>
                    <Button onClick={handlePurgeOldMessages} className={classes.purgeButton}>
                        {i18n.t("files.messagesTab.purgeConfirm")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal de Creación / Edición de Archivo */}
            <FileModal
                open={fileListModalOpen}
                onClose={handleCloseFileListModal}
                reload={fetchFileLists}
                aria-labelledby="form-dialog-title"
                fileListId={selectedFileList && selectedFileList.id}
            />

            {user.profile === "user" ? (
                <ForbiddenPage />
            ) : (
                <Paper className={classes.mainPaper} variant="outlined">
                    <MainHeader>
                        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                            <PageHelpGuide
                                helpData={FILES_HELP_DATA}
                                tooltipText="Guía y Ayuda de Archivos (Haz clic para ver)"
                            />
                            <Title>{i18n.t("files.title")}</Title>
                        </div>
                        <MainHeaderButtonsWrapper>
                            {activeTab === 0 && (
                                <>
                                    <TextField
                                        placeholder={i18n.t("contacts.searchPlaceholder")}
                                        type="search"
                                        size="small"
                                        value={searchParam}
                                        onChange={(e) => setSearchParam(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon color="secondary" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <Button
                                        className={classes.applyButton}
                                        onClick={handleOpenFileListModal}
                                        disabled={isStorageFull}
                                    >
                                        {i18n.t("files.buttons.add")}
                                    </Button>
                                </>
                            )}
                            {activeTab === 1 && (
                                <Button
                                    className={classes.purgeButton}
                                    startIcon={<DeleteSweepIcon />}
                                    onClick={() => setPurgeDialogOpen(true)}
                                >
                                    {i18n.t("files.messagesTab.purgeButton")}
                                </Button>
                            )}
                        </MainHeaderButtonsWrapper>
                    </MainHeader>

                    {/* ALERTA CRÍTICA: Almacenamiento Agotado (Uniforme en Capa 3) */}
                    {isStorageFull && (
                        <div className={classes.alertCard}>
                            <Box display="flex" alignItems="center" gap={1.5} style={{ flex: 1 }}>
                                <WarningIcon style={{ color: "#e84118", fontSize: 26 }} />
                                <div>
                                    <Typography variant="subtitle2" style={{ fontWeight: "bold", color: "#e84118" }}>
                                        {i18n.t("files.storageWarning.title")}
                                    </Typography>
                                    <Typography variant="body2" style={{ fontSize: 13 }}>
                                        {i18n.t("files.storageWarning.message")}
                                    </Typography>
                                </div>
                            </Box>
                        </div>
                    )}

                    {/* Storage Stats Section interactiva, compacta y estilizada */}
                    {storageStats && (
                        <Paper className={classes.statsPaper} variant="outlined">
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <AssessmentIcon style={{ fontSize: 18, color: "#00b0ff" }} />
                                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                                        {i18n.t("files.storage.title") || "Almacenamiento"}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                                        {i18n.t("files.storage.used") || "Usado"}: <strong>{formatBytes(storageStats.used)}</strong> / {formatBytes(storageStats.limit)}
                                    </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography
                                        variant="caption"
                                        style={{
                                            fontWeight: "bold",
                                            color: storageStats.used >= storageStats.limit ? "#e84118" : "#00b0ff"
                                        }}
                                    >
                                        {getStoragePercentage(storageStats.used, storageStats.limit)}
                                    </Typography>
                                    <IconButton size="small" onClick={fetchStorageStats} style={{ padding: 4 }}>
                                        <RefreshIcon style={{ fontSize: 16 }} />
                                    </IconButton>
                                </Box>
                            </Box>

                            <Box width="100%" mb={1}>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min((storageStats.used / (storageStats.limit || 1)) * 100, 100)}
                                    color={storageStats.used >= storageStats.limit ? "secondary" : "primary"}
                                    style={{ height: 5, borderRadius: 3 }}
                                />
                            </Box>

                            <Grid container spacing={1}>
                                <Grid item xs={6} sm={4} md={2} onClick={() => setActiveTab(0)}>
                                    <div className={activeTab === 0 ? classes.activeMetric : classes.clickableMetric}>
                                        <Typography variant="caption" color="textSecondary">{i18n.t("files.storage.breakdown.files")}</Typography>
                                        <Typography variant="body2" style={{ fontWeight: 600 }}>{formatBytes(storageStats.breakdown.fileList)}</Typography>
                                        <Typography variant="caption" color="textSecondary" style={{ fontSize: 10, display: "block" }}>
                                            {storageStats.counts?.fileList || files.length || 0} {i18n.t("files.storage.filesCountLabel") || "archivos"}
                                        </Typography>
                                    </div>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2} onClick={() => setActiveTab(1)}>
                                    <div className={activeTab === 1 ? classes.activeMetric : classes.clickableMetric} style={{ borderBottom: "2px solid #e84118" }}>
                                        <Typography variant="caption" style={{ color: "#e84118", fontWeight: "bold" }}>
                                            {i18n.t("files.storage.breakdown.messages")} 🔥
                                        </Typography>
                                        <Typography variant="body2" style={{ fontWeight: 700, color: "#e84118" }}>
                                            {formatBytes(storageStats.breakdown.messages)}
                                        </Typography>
                                        <Typography variant="caption" style={{ fontSize: 10, color: "#e84118", display: "block" }}>
                                            {storageStats.counts?.messages || messagesCount || 0} {i18n.t("files.storage.filesCountLabel") || "archivos"}
                                        </Typography>
                                    </div>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2} onClick={() => setActiveTab(2)}>
                                    <div className={activeTab === 2 ? classes.activeMetric : classes.clickableMetric}>
                                        <Typography variant="caption" color="textSecondary">{i18n.t("files.storage.breakdown.campaigns")}</Typography>
                                        <Typography variant="body2" style={{ fontWeight: 600 }}>{formatBytes(storageStats.breakdown.campaigns)}</Typography>
                                        <Typography variant="caption" color="textSecondary" style={{ fontSize: 10, display: "block" }}>
                                            {storageStats.counts?.campaigns || 0} {i18n.t("files.storage.filesCountLabel") || "archivos"}
                                        </Typography>
                                    </div>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2} onClick={() => setActiveTab(3)}>
                                    <div className={activeTab === 3 ? classes.activeMetric : classes.clickableMetric}>
                                        <Typography variant="caption" color="textSecondary">{i18n.t("files.storage.breakdown.flow")}</Typography>
                                        <Typography variant="body2" style={{ fontWeight: 600 }}>{formatBytes(storageStats.breakdown.flow)}</Typography>
                                        <Typography variant="caption" color="textSecondary" style={{ fontSize: 10, display: "block" }}>
                                            {storageStats.counts?.flow || 0} {i18n.t("files.storage.filesCountLabel") || "archivos"}
                                        </Typography>
                                    </div>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2} onClick={() => setActiveTab(4)}>
                                    <div className={activeTab === 4 ? classes.activeMetric : classes.clickableMetric}>
                                        <Typography variant="caption" color="textSecondary">{i18n.t("files.storage.breakdown.quickMessage")}</Typography>
                                        <Typography variant="body2" style={{ fontWeight: 600 }}>{formatBytes(storageStats.breakdown.quickMessage)}</Typography>
                                        <Typography variant="caption" color="textSecondary" style={{ fontSize: 10, display: "block" }}>
                                            {storageStats.counts?.quickMessage || 0} {i18n.t("files.storage.filesCountLabel") || "archivos"}
                                        </Typography>
                                    </div>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2} onClick={() => setActiveTab(5)}>
                                    <div className={activeTab === 5 ? classes.activeMetric : classes.clickableMetric}>
                                        <Typography variant="caption" color="textSecondary">{i18n.t("files.storage.breakdown.contactsUsers") || "Contactos & Usuarios"}</Typography>
                                        <Typography variant="body2" style={{ fontWeight: 600 }}>{formatBytes((storageStats.breakdown.contacts || 0) + (storageStats.breakdown.user || 0))}</Typography>
                                        <Typography variant="caption" color="textSecondary" style={{ fontSize: 10, display: "block" }}>
                                            {((storageStats.counts?.contacts || 0) + (storageStats.counts?.user || 0))} {i18n.t("files.storage.filesCountLabel") || "archivos"}
                                        </Typography>
                                    </div>
                                </Grid>
                            </Grid>
                        </Paper>
                    )}

                    {/* Barra de Pestañas Homologada del Sistema (StandardTabs) */}
                    <div style={{ width: "100%", marginBottom: 12 }}>
                        <StandardTabs
                            value={activeTab}
                            onChange={(e, newTab) => setActiveTab(newTab)}
                            fullWidth
                            items={[
                                {
                                    value: 0,
                                    label: i18n.t("files.tabs.filesShort") || i18n.t("files.tabs.files"),
                                    badge: storageStats?.counts?.fileList ?? files.length,
                                    icon: <FolderIcon style={{ fontSize: 17 }} />
                                },
                                {
                                    value: 1,
                                    label: i18n.t("files.tabs.messagesShort") || i18n.t("files.tabs.messages"),
                                    badge: storageStats?.counts?.messages ?? messagesCount ?? 0,
                                    icon: <ChatIcon style={{ fontSize: 17 }} />
                                },
                                {
                                    value: 2,
                                    label: i18n.t("files.tabs.campaignsShort") || i18n.t("files.tabs.campaigns"),
                                    badge: storageStats?.counts?.campaigns ?? 0,
                                    icon: <SendIcon style={{ fontSize: 17 }} />
                                },
                                {
                                    value: 3,
                                    label: i18n.t("files.tabs.flowShort") || i18n.t("files.tabs.flow"),
                                    badge: storageStats?.counts?.flow ?? 0,
                                    icon: <AccountTreeIcon style={{ fontSize: 17 }} />
                                },
                                {
                                    value: 4,
                                    label: i18n.t("files.tabs.quickMessagesShort") || i18n.t("files.tabs.quickMessages"),
                                    badge: storageStats?.counts?.quickMessage ?? 0,
                                    icon: <QuestionAnswerIcon style={{ fontSize: 17 }} />
                                },
                                {
                                    value: 5,
                                    label: i18n.t("files.tabs.contactsUsersShort") || i18n.t("files.tabs.contactsUsers"),
                                    badge: ((storageStats?.counts?.contacts || 0) + (storageStats?.counts?.user || 0)),
                                    icon: <PeopleIcon style={{ fontSize: 17 }} />
                                }
                            ]}
                        />
                    </div>

                    {/* VISTA TAB 0: ARCHIVOS (RAG / BIBLIOTECA) */}
                    {activeTab === 0 && (
                        <>
                            {selectedFileIds.length > 0 && (
                                <div className={classes.batchActionBar}>
                                    <Typography variant="body2" style={{ fontWeight: "bold" }}>
                                        {i18n.t("files.bulk.selectedCount", {
                                            count: selectedFileIds.length,
                                            size: formatBytes(selectedFilesTotalSize)
                                        })}
                                    </Typography>
                                    <Button
                                        size="small"
                                        startIcon={<DeleteOutlineIcon />}
                                        className={classes.dangerButton}
                                        onClick={() => setBatchDeleteFilesModalOpen(true)}
                                    >
                                        {i18n.t("files.bulk.deleteSelected")}
                                    </Button>
                                </div>
                            )}

                            <Box display="flex" style={{ flex: 1, overflow: "hidden", gap: 16 }}>
                                <Paper className={classes.tablePaper} elevation={0} style={{ minWidth: 0 }}>
                                    <TableContainer className={classes.scrollArea}>
                                            <Table stickyHeader size="small" style={{ tableLayout: "fixed", width: "100%" }}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell padding="checkbox" className={classes.tableHeader} align="center" style={{ width: 44 }}>
                                                            <Checkbox
                                                                indeterminate={selectedFileIds.length > 0 && selectedFileIds.length < files.length}
                                                                checked={files.length > 0 && selectedFileIds.length === files.length}
                                                                onChange={handleSelectAllFiles}
                                                                color="primary"
                                                            />
                                                        </TableCell>
                                                        <TableCell className={classes.tableHeader}>
                                                            <TableSortLabel
                                                                active={filesOrderBy === "name"}
                                                                direction={filesOrderBy === "name" ? filesOrder : "asc"}
                                                                onClick={() => handleRequestSortFiles("name")}
                                                                className={classes.tableHeaderSort}
                                                            >
                                                                {i18n.t("files.table.name")}
                                                            </TableSortLabel>
                                                        </TableCell>
                                                        <TableCell className={classes.tableHeader} align="center" style={{ width: 115 }}>
                                                            <TableSortLabel
                                                                active={filesOrderBy === "size"}
                                                                direction={filesOrderBy === "size" ? filesOrder : "asc"}
                                                                onClick={() => handleRequestSortFiles("size")}
                                                                className={classes.tableHeaderSort}
                                                            >
                                                                {i18n.t("files.table.size")}
                                                            </TableSortLabel>
                                                        </TableCell>
                                                        <TableCell className={classes.tableHeader} align="center" style={{ width: selectedFileForPreview ? 125 : 140 }}>
                                                            <TableSortLabel
                                                                active={filesOrderBy === "createdAt"}
                                                                direction={filesOrderBy === "createdAt" ? filesOrder : "asc"}
                                                                onClick={() => handleRequestSortFiles("createdAt")}
                                                                className={classes.tableHeaderSort}
                                                            >
                                                                {i18n.t("files.table.date")}
                                                            </TableSortLabel>
                                                        </TableCell>
                                                        <TableCell className={classes.tableHeader} align="center" style={{ width: 85, padding: "6px 8px" }}>{i18n.t("files.table.actions")}</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {sortedFiles.map((fileList) => (
                                                        <TableRow
                                                            key={fileList.id}
                                                            hover
                                                            selected={selectedFileForPreview && selectedFileForPreview.id === fileList.id}
                                                            onClick={() => setSelectedFileId(fileList.id)}
                                                            style={{ cursor: "pointer" }}
                                                        >
                                                            <TableCell padding="checkbox" align="center" onClick={(e) => e.stopPropagation()}>
                                                                <Checkbox
                                                                    checked={selectedFileIds.includes(fileList.id)}
                                                                    onChange={() => handleToggleSelectFile(fileList.id)}
                                                                    color="primary"
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box display="flex" alignItems="center" gap={1}>
                                                                    <FolderIcon style={{ color: "#00b0ff" }} />
                                                                    <Typography variant="body2" style={{ fontWeight: 600 }}>{fileList.name}</Typography>
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <div className={classes.sizePill}>
                                                                    {formatBytes(getFileListTotalSize(fileList))}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Typography variant="caption" color="textSecondary">
                                                                    {fileList.createdAt ? moment(fileList.createdAt).format("DD/MM/YYYY HH:mm") : "—"}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="center" style={{ width: 85, padding: "6px 4px" }}>
                                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditFileList(fileList); }}>
                                                                    <EditIcon style={{ color: "#00a8ff" }} />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setConfirmModalOpen(true);
                                                                        setDeletingFileList(fileList);
                                                                    }}
                                                                >
                                                                    <DeleteOutlineIcon style={{ color: "#e84118" }} />
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                    {loading && <TableRowSkeleton columns={5} />}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Paper>

                                    {selectedFileForPreview && (
                                        <Paper className={classes.sidebarPaper} variant="outlined">
                                            <div className={classes.scrollArea}>
                                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} p={1}>
                                                    <Typography variant="h6">{selectedFileForPreview.name}</Typography>
                                                    <IconButton size="small" onClick={() => setSelectedFileId(null)}>
                                                        <CloseIcon style={{ color: theme.palette.text.secondary }} />
                                                    </IconButton>
                                                </Box>
                                                <Divider />
                                                <Box mt={2}>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        {i18n.t("files.sidebar.media")}
                                                    </Typography>

                                                    {selectedFileForPreview.options && selectedFileForPreview.options.length > 0 ? (
                                                        <List>
                                                            {selectedFileForPreview.options.map((option, index) => (
                                                                <Box key={index} mb={3} p={1} border={1} borderColor="grey.300" borderRadius={4}>
                                                                    <Typography variant="body2" style={{ fontWeight: "bold" }}>{option.name}</Typography>

                                                                    <Box mt={1} display="flex" justifyContent="center" flexDirection="column" alignItems="center">
                                                                        {option.path && (option.path.endsWith('.jpg') || option.path.endsWith('.png') || option.path.endsWith('.jpeg') || option.mediaType?.startsWith('image')) ? (
                                                                            <ImageWithFallback src={getFileUrl(option.path)} alt={option.name} className={classes.filePreview} />
                                                                        ) : option.path && (option.path.endsWith('.mp4') || option.mediaType?.startsWith('video')) ? (
                                                                            <VideoWithFallback src={getFileUrl(option.path)} className={classes.filePreview} />
                                                                        ) : option.path && (option.path.endsWith('.pdf') || option.mediaType?.includes('pdf')) ? (
                                                                            <iframe
                                                                                src={`${getFileUrl(option.path)}#toolbar=0`}
                                                                                title={option.name}
                                                                                className={classes.pdfPreview}
                                                                            />
                                                                        ) : option.path && (option.path.endsWith('.mp3') || option.path.endsWith('.ogg') || option.path.endsWith('.wav') || option.mediaType?.startsWith('audio')) ? (
                                                                            <audio controls className={classes.audioPreview}>
                                                                                <source src={getFileUrl(option.path)} type={option.mediaType || "audio/mpeg"} />
                                                                            </audio>
                                                                        ) : (
                                                                            <FolderIcon style={{ fontSize: 60, color: "#ccc" }} />
                                                                        )}
                                                                    </Box>

                                                                    <TextField
                                                                        label={i18n.t("files.sidebar.tag")}
                                                                        value={`/files/${option.path.split('/').pop()}`}
                                                                        variant="outlined"
                                                                        size="small"
                                                                        fullWidth
                                                                        margin="dense"
                                                                        style={{ marginTop: 12 }}
                                                                        InputProps={{
                                                                            readOnly: true,
                                                                            endAdornment: (
                                                                                <InputAdornment position="end">
                                                                                    <IconButton onClick={() => copyToClipboard(`/files/${option.path.split('/').pop()}`)} edge="end">
                                                                                        <FileCopyIcon />
                                                                                    </IconButton>
                                                                                </InputAdornment>
                                                                            ),
                                                                        }}
                                                                        className={classes.copyInput}
                                                                    />
                                                                    {option.size > 0 && (
                                                                        <Typography variant="caption" display="block" color="textSecondary">
                                                                            Size: {formatBytes(option.size)}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            ))}
                                                        </List>
                                                    ) : (
                                                        <Typography variant="body2" color="textSecondary">{i18n.t("files.sidebar.noMedia")}</Typography>
                                                    )}
                                                </Box>
                                            </div>
                                        </Paper>
                                    )}
                                </Box>
                        </>
                    )}

                    {/* VISTA TAB 1: MENSAJES (DEPURADOR DE CHATS - 4.46 GB) */}
                    {activeTab === 1 && (
                        <Box display="flex" flexDirection="column" style={{ flex: 1, overflow: "hidden" }}>
                            {/* Barra de Filtros y Búsqueda para Mensajes */}
                            <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.5} pt={1} pb={1.5}>
                                <TextField
                                    placeholder={i18n.t("contacts.searchPlaceholder")}
                                    size="small"
                                    variant="outlined"
                                    value={searchMessageParam}
                                    onChange={(e) => setSearchMessageParam(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    style={{ width: 220 }}
                                />

                                <FormControl variant="outlined" size="small" className={classes.filterControl}>
                                    <InputLabel id="filter-age-label">{i18n.t("files.messagesTab.filterAge")}</InputLabel>
                                    <Select
                                        labelId="filter-age-label"
                                        value={filterAge}
                                        onChange={(e) => setFilterAge(e.target.value)}
                                        label={i18n.t("files.messagesTab.filterAge")}
                                    >
                                        <MenuItem value="all">{i18n.t("files.messagesTab.ageAll")}</MenuItem>
                                        <MenuItem value="30">{i18n.t("files.messagesTab.age30")}</MenuItem>
                                        <MenuItem value="60">{i18n.t("files.messagesTab.age60")}</MenuItem>
                                        <MenuItem value="90">{i18n.t("files.messagesTab.age90")}</MenuItem>
                                    </Select>
                                </FormControl>

                                <FormControl variant="outlined" size="small" className={classes.filterControl}>
                                    <InputLabel id="filter-type-label">{i18n.t("files.messagesTab.filterType")}</InputLabel>
                                    <Select
                                        labelId="filter-type-label"
                                        value={filterType}
                                        onChange={(e) => setFilterType(e.target.value)}
                                        label={i18n.t("files.messagesTab.filterType")}
                                    >
                                        <MenuItem value="all">{i18n.t("files.messagesTab.typeAll")}</MenuItem>
                                        <MenuItem value="audio">{i18n.t("files.messagesTab.typeAudio")}</MenuItem>
                                        <MenuItem value="video">{i18n.t("files.messagesTab.typeVideo")}</MenuItem>
                                        <MenuItem value="image">{i18n.t("files.messagesTab.typeImage")}</MenuItem>
                                        <MenuItem value="application">{i18n.t("files.messagesTab.typeDocument")}</MenuItem>
                                    </Select>
                                </FormControl>

                                <Typography variant="body2" color="textSecondary" style={{ marginLeft: "auto" }}>
                                    Total: <strong>{messagesCount}</strong> archivos multimedia encontrados
                                </Typography>
                            </Box>

                            {/* Barra de Acciones por Lote para Mensajes */}
                            {selectedMessageIds.length > 0 && (
                                <div className={classes.batchActionBar}>
                                    <Typography variant="body2" style={{ fontWeight: "bold" }}>
                                        {i18n.t("files.bulk.selectedCount", {
                                            count: selectedMessageIds.length,
                                            size: "archivos marcados"
                                        })}
                                    </Typography>
                                    <Button
                                        size="small"
                                        startIcon={<DeleteOutlineIcon />}
                                        className={classes.dangerButton}
                                        onClick={() => setBatchDeleteMessagesModalOpen(true)}
                                    >
                                        {i18n.t("files.bulk.deleteSelected")}
                                    </Button>
                                </div>
                            )}

                            <Box display="flex" style={{ flex: 1, overflow: "hidden", gap: 16 }}>
                                <Paper className={classes.tablePaper} elevation={0} style={{ minWidth: 0 }}>
                                    <TableContainer className={classes.scrollArea}>
                                            <Table stickyHeader size="small" style={{ tableLayout: "fixed", width: "100%", minWidth: selectedMessageForPreview ? 700 : 850 }}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell padding="checkbox" className={classes.tableHeader} align="center" style={{ width: 44 }}>
                                                            <Checkbox
                                                                indeterminate={selectedMessageIds.length > 0 && selectedMessageIds.length < messages.length}
                                                                checked={messages.length > 0 && selectedMessageIds.length === messages.length}
                                                                onChange={handleSelectAllMessages}
                                                                color="primary"
                                                            />
                                                        </TableCell>
                                                        <TableCell className={classes.tableHeader} align="center" style={{ width: 48 }}>{i18n.t("files.table.type")}</TableCell>
                                                        <TableCell className={classes.tableHeader} style={{ width: selectedMessageForPreview ? 190 : 220 }}>
                                                            <TableSortLabel
                                                                active={messagesOrderBy === "contact"}
                                                                direction={messagesOrderBy === "contact" ? messagesOrder : "asc"}
                                                                onClick={() => handleRequestSortMessages("contact")}
                                                                className={classes.tableHeaderSort}
                                                            >
                                                                {i18n.t("files.table.contact")}
                                                            </TableSortLabel>
                                                        </TableCell>
                                                        <TableCell className={classes.tableHeader}>
                                                            <TableSortLabel
                                                                active={messagesOrderBy === "name"}
                                                                direction={messagesOrderBy === "name" ? messagesOrder : "asc"}
                                                                onClick={() => handleRequestSortMessages("name")}
                                                                className={classes.tableHeaderSort}
                                                            >
                                                                {i18n.t("files.table.name")}
                                                            </TableSortLabel>
                                                        </TableCell>
                                                        <TableCell className={classes.tableHeader} align="center" style={{ width: 115 }}>
                                                            <TableSortLabel
                                                                active={messagesOrderBy === "size"}
                                                                direction={messagesOrderBy === "size" ? messagesOrder : "asc"}
                                                                onClick={() => handleRequestSortMessages("size")}
                                                                className={classes.tableHeaderSort}
                                                            >
                                                                {i18n.t("files.table.size")}
                                                            </TableSortLabel>
                                                        </TableCell>
                                                        <TableCell className={classes.tableHeader} align="center" style={{ width: 125 }}>
                                                            <TableSortLabel
                                                                active={messagesOrderBy === "createdAt"}
                                                                direction={messagesOrderBy === "createdAt" ? messagesOrder : "asc"}
                                                                onClick={() => handleRequestSortMessages("createdAt")}
                                                                className={classes.tableHeaderSort}
                                                            >
                                                                {i18n.t("files.table.date")}
                                                            </TableSortLabel>
                                                        </TableCell>
                                                        <TableCell className={classes.tableHeader} align="center" style={{ width: 85, padding: "6px 4px" }}>{i18n.t("files.table.actions")}</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {sortedMessages.map((msg) => (
                                                        <TableRow
                                                            key={msg.id}
                                                            hover
                                                            selected={selectedMessageForPreview && selectedMessageForPreview.id === msg.id}
                                                            onClick={() => setSelectedMessageId(msg.id)}
                                                            style={{ cursor: "pointer" }}
                                                        >
                                                            <TableCell padding="checkbox" align="center" onClick={(e) => e.stopPropagation()}>
                                                                <Checkbox
                                                                    checked={selectedMessageIds.includes(msg.id)}
                                                                    onChange={() => handleToggleSelectMessage(msg.id)}
                                                                    color="primary"
                                                                />
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {renderMediaTypeIcon(msg.mediaType)}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" style={{ fontWeight: 600 }} noWrap>
                                                                    {msg.contact?.name || msg.contact?.number || (msg.fromMe ? "Saliente" : "Entrante")}
                                                                </Typography>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    Ticket #{msg.ticketId}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" style={{ fontWeight: 600 }} noWrap>
                                                                    {msg.fileName || (msg.mediaUrl ? msg.mediaUrl.split("/").pop() : msg.body)}
                                                                </Typography>
                                                                {msg.body && msg.body !== msg.fileName && (
                                                                    <Typography variant="caption" color="textSecondary" noWrap style={{ display: "block", maxWidth: selectedMessageForPreview ? 180 : 280 }}>
                                                                        {msg.body}
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <div className={(msg.fileSize || 0) > 1024 * 1024 ? classes.sizePillHeavy : classes.sizePill}>
                                                                    {formatBytes(msg.fileSize || 0)}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Typography variant="caption" color="textSecondary" noWrap>
                                                                    {moment(msg.createdAt).format("DD/MM/YYYY HH:mm")}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="center" style={{ width: 85, padding: "6px 4px" }} onClick={(e) => e.stopPropagation()}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => {
                                                                        setDeletingMessage(msg);
                                                                        setConfirmDeleteMessageModalOpen(true);
                                                                    }}
                                                                >
                                                                    <DeleteOutlineIcon style={{ color: "#e84118" }} />
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                    {messagesLoading && <TableRowSkeleton columns={7} />}
                                                    {!messagesLoading && messages.length === 0 && (
                                                        <TableRow>
                                                            <TableCell colSpan={7} align="center" style={{ padding: 32 }}>
                                                                <Typography variant="body2" color="textSecondary">
                                                                    {i18n.t("files.messagesTab.empty")}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Paper>

                                    {selectedMessageForPreview && (
                                        <Paper className={classes.sidebarPaper} variant="outlined">
                                            <div className={classes.scrollArea}>
                                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} p={1}>
                                                    <Typography variant="subtitle1" style={{ fontWeight: 700 }} noWrap>
                                                        {selectedMessageForPreview.fileName || (selectedMessageForPreview.mediaUrl ? selectedMessageForPreview.mediaUrl.split("/").pop() : selectedMessageForPreview.body)}
                                                    </Typography>
                                                    <IconButton size="small" onClick={() => setSelectedMessageId(null)}>
                                                        <CloseIcon style={{ color: theme.palette.text.secondary }} />
                                                    </IconButton>
                                                </Box>
                                                <Divider />

                                                <Box mt={2} p={1}>
                                                    <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                                                        {i18n.t("files.table.contact")}:
                                                    </Typography>
                                                    <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 8 }}>
                                                        {selectedMessageForPreview.contact?.name || selectedMessageForPreview.contact?.number || (selectedMessageForPreview.fromMe ? "Saliente" : "Entrante")}
                                                        {" · "}Ticket #{selectedMessageForPreview.ticketId}
                                                    </Typography>

                                                    {/* Vista previa multimedia */}
                                                    <Box
                                                        p={1}
                                                        border={1}
                                                        borderColor={theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)"}
                                                        borderRadius={8}
                                                        display="flex"
                                                        flexDirection="column"
                                                        alignItems="center"
                                                        justifyContent="center"
                                                        style={{ backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)", minHeight: 140 }}
                                                    >
                                                        {selectedMessageForPreview.fileSize === 0 ? (
                                                            <FileNotFoundFallback />
                                                        ) : selectedMessageForPreview.mediaType?.includes("image") || (selectedMessageForPreview.mediaUrl && /\.(jpg|jpeg|png|webp|gif)$/i.test(selectedMessageForPreview.mediaUrl)) ? (
                                                            <ImageWithFallback
                                                                src={getFileUrl(selectedMessageForPreview.mediaUrl)}
                                                                alt={selectedMessageForPreview.fileName || "Preview"}
                                                                className={classes.filePreview}
                                                                style={{ maxHeight: 170, maxWidth: 220, objectFit: "contain", borderRadius: 8 }}
                                                            />
                                                        ) : selectedMessageForPreview.mediaType?.includes("video") || (selectedMessageForPreview.mediaUrl && /\.(mp4|webm|mov)$/i.test(selectedMessageForPreview.mediaUrl)) ? (
                                                            <VideoWithFallback
                                                                src={getFileUrl(selectedMessageForPreview.mediaUrl)}
                                                                className={classes.filePreview}
                                                                style={{ maxHeight: 170, maxWidth: 240, width: "100%", borderRadius: 8 }}
                                                            />
                                                        ) : selectedMessageForPreview.mediaType?.includes("audio") || (selectedMessageForPreview.mediaUrl && /\.(mp3|ogg|wav|opus|m4a)$/i.test(selectedMessageForPreview.mediaUrl)) ? (
                                                            <Box width="100%" display="flex" flexDirection="column" alignItems="center" p={2}>
                                                                <AudiotrackIcon style={{ fontSize: 48, color: "#ff9f43", marginBottom: 8 }} />
                                                                <audio controls style={{ width: "100%" }}>
                                                                    <source src={getFileUrl(selectedMessageForPreview.mediaUrl)} />
                                                                </audio>
                                                            </Box>
                                                        ) : selectedMessageForPreview.mediaUrl && /\.pdf$/i.test(selectedMessageForPreview.mediaUrl) ? (
                                                            <Box width="100%" display="flex" flexDirection="column" alignItems="center" p={2}>
                                                                <DescriptionIcon style={{ fontSize: 48, color: "#e84118", marginBottom: 8 }} />
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    color="primary"
                                                                    href={getFileUrl(selectedMessageForPreview.mediaUrl)}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    Abrir PDF
                                                                </Button>
                                                            </Box>
                                                        ) : (
                                                            <Box width="100%" display="flex" flexDirection="column" alignItems="center" p={2}>
                                                                <DescriptionIcon style={{ fontSize: 48, color: "#5f27cd", marginBottom: 8 }} />
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    color="primary"
                                                                    href={getFileUrl(selectedMessageForPreview.mediaUrl)}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    Ver / Descargar Archivo
                                                                </Button>
                                                            </Box>
                                                        )}
                                                    </Box>

                                                    {selectedMessageForPreview.body && selectedMessageForPreview.body !== selectedMessageForPreview.fileName && (
                                                        <Box mt={2}>
                                                            <Typography variant="caption" color="textSecondary">
                                                                Mensaje / Leyenda:
                                                            </Typography>
                                                            <Typography variant="body2" style={{ backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#f5f5f5", padding: "6px 10px", borderRadius: 6, marginTop: 4 }}>
                                                                {selectedMessageForPreview.body}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                                                        <div>
                                                            <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                                                                {i18n.t("files.table.size")}:
                                                            </Typography>
                                                            <div className={(selectedMessageForPreview.fileSize || 0) > 1024 * 1024 ? classes.sizePillHeavy : classes.sizePill} style={{ marginTop: 2 }}>
                                                                {formatBytes(selectedMessageForPreview.fileSize || 0)}
                                                            </div>
                                                        </div>
                                                        <div style={{ textAlign: "right" }}>
                                                            <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                                                                {i18n.t("files.table.date")}:
                                                            </Typography>
                                                            <Typography variant="caption" style={{ fontWeight: 600 }}>
                                                                {moment(selectedMessageForPreview.createdAt).format("DD/MM/YYYY HH:mm")}
                                                            </Typography>
                                                        </div>
                                                    </Box>

                                                    <Box mt={3}>
                                                        <Button
                                                            fullWidth
                                                            variant="outlined"
                                                            style={{ color: "#e84118", borderColor: "#e84118" }}
                                                            startIcon={<DeleteOutlineIcon />}
                                                            onClick={() => {
                                                                setDeletingMessage(selectedMessageForPreview);
                                                                setConfirmDeleteMessageModalOpen(true);
                                                            }}
                                                        >
                                                            Eliminar Multimedia
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            </div>
                                        </Paper>
                                    )}
                                </Box>
                        </Box>
                    )}

                    {/* VISTA TAB 2: CAMPAÑAS MASIVAS */}
                    {activeTab === 2 && (
                        <Paper className={classes.infoPaper} variant="outlined">
                            <SendIcon style={{ fontSize: 48, color: "#00b0ff", marginBottom: 12 }} />
                            <Typography variant="h6" gutterBottom>
                                {i18n.t("files.tabs.campaigns")} ({formatBytes(storageStats?.breakdown?.campaigns || 0)})
                            </Typography>
                            <Typography variant="body2" color="textSecondary" style={{ maxWidth: 600, margin: "0 auto 16px" }}>
                                {i18n.t("files.protected.campaignsNotice")}
                            </Typography>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => history.push("/campaigns")}
                            >
                                {i18n.t("files.protected.campaignsBtn")}
                            </Button>
                        </Paper>
                    )}

                    {/* VISTA TAB 3: FLUJOS (FLOW BUILDER - PROTEGIDO) */}
                    {activeTab === 3 && (
                        <Paper className={classes.infoPaper} variant="outlined">
                            <AccountTreeIcon style={{ fontSize: 48, color: "#ff9f43", marginBottom: 12 }} />
                            <Typography variant="h6" gutterBottom>
                                {i18n.t("files.tabs.flow")} ({formatBytes(storageStats?.breakdown?.flow || 0)})
                            </Typography>
                            <Typography variant="body2" color="textSecondary" style={{ maxWidth: 650, margin: "0 auto 16px" }}>
                                🛡️ {i18n.t("files.protected.flowNotice")}
                            </Typography>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => history.push("/flowbuilders")}
                            >
                                {i18n.t("files.protected.flowBtn")}
                            </Button>
                        </Paper>
                    )}

                    {/* VISTA TAB 4: RESPUESTAS RÁPIDAS (PROTEGIDO) */}
                    {activeTab === 4 && (
                        <Paper className={classes.infoPaper} variant="outlined">
                            <QuestionAnswerIcon style={{ fontSize: 48, color: "#10ac84", marginBottom: 12 }} />
                            <Typography variant="h6" gutterBottom>
                                {i18n.t("files.tabs.quickMessages")} ({formatBytes(storageStats?.breakdown?.quickMessage || 0)})
                            </Typography>
                            <Typography variant="body2" color="textSecondary" style={{ maxWidth: 600, margin: "0 auto 16px" }}>
                                🛡️ {i18n.t("files.protected.quickMessageNotice")}
                            </Typography>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => history.push("/quick-messages")}
                            >
                                {i18n.t("files.protected.quickMessagesBtn")}
                            </Button>
                        </Paper>
                    )}

                    {/* VISTA TAB 5: CONTACTOS Y USUARIOS */}
                    {activeTab === 5 && (
                        <Paper className={classes.infoPaper} variant="outlined">
                            <PeopleIcon style={{ fontSize: 48, color: "#5f27cd", marginBottom: 12 }} />
                            <Typography variant="h6" gutterBottom>
                                {i18n.t("files.tabs.contactsUsers")} ({formatBytes((storageStats?.breakdown?.contacts || 0) + (storageStats?.breakdown?.user || 0))})
                            </Typography>
                            <Typography variant="body2" color="textSecondary" style={{ maxWidth: 600, margin: "0 auto 16px" }}>
                                {i18n.t("files.protected.contactsNotice")}
                            </Typography>
                            <Box display="flex" justifyContent="center" alignItems="center">
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    style={{ margin: "0 10px" }}
                                    onClick={() => history.push("/contacts")}
                                >
                                    {i18n.t("files.protected.viewContactsBtn")}
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    style={{ margin: "0 10px" }}
                                    onClick={() => history.push("/users")}
                                >
                                    {i18n.t("files.protected.viewUsersBtn")}
                                </Button>
                            </Box>
                        </Paper>
                    )}
                </Paper>
            )}
        </MainContainer>
    );
};

export default FileLists;
